/**
 * Utility functions for handling URL parameters
 */

/**
 * Get URL parameters with default values
 * @param {Object} options - Configuration options
 * @param {string} [options.idParam='id'] - The name of the ID parameter
 * @param {string} [options.idDefault='SA'] - Default value for ID if not found
 * @param {string} [options.vidParam='vid'] - The name of the variant ID parameter
 * @param {string} [options.vidDefault=''] - Default value for variant ID if not found
 * @param {string} [options.userParam='user'] - The name of the user parameter
 * @param {string} [options.userDefault='DefaultUser'] - Default value for user if not found
 * @returns {Object} Object containing id, vid, and user values
 */
export const getUrlParams = (options = {}) => {
  const {
    idParam = 'id',
    idDefault = 'SA',
    vidParam = 'vid',
    vidDefault = '',
    userParam = 'user',
    userDefault = 'DefaultUser'
  } = options;

  const urlParams = new URLSearchParams(window.location.search);
  // Read common params
  const rawId = urlParams.get(idParam) || idDefault;
  const vid = urlParams.get(vidParam) || vidDefault;
  const user = urlParams.get(userParam) || userDefault;
  // Additional optional params for WeChat / Weimob flows
  const wid = urlParams.get('wid') || '';
  const pid = urlParams.get('pid') || '';
  const app_id = urlParams.get('app_id') || urlParams.get('appId') || '';

  // IMPORTANT: Prefer pid over id when resolving the effective product identifier
  // If pid is present, use it as the product id for loading config; otherwise fall back to id
  const id = pid || rawId;

  return { id, vid, user, wid, pid, app_id };
};

/**
 * Add timestamp to URL to prevent caching
 * @param {string} url - The URL to add timestamp to
 * @returns {string} URL with timestamp parameter
 */
export const addTimestamp = (url) => {
  const timestamp = new Date().getTime();
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}t=${timestamp}`;
};

/**
 * Generate product config URL
 * @param {string} id - Product ID
 * @param {string} [user] - Optional user parameter
 * @returns {string} Complete URL with timestamp
 */
export const getProductConfigUrl = (id, user) => {
  // If user is not provided, get it from the URL parameters
  if (!user) {
    const { user: urlUser } = getUrlParams();
    user = urlUser; // Will default to 'DefaultUser' via getUrlParams
  }
  
  const baseUrl = `https://dev-bucket-plugcustom.oss-cn-hongkong.aliyuncs.com/${user}/ProductConfigs/${id}.json?t=${new Date().getTime()}`;
  return baseUrl;
};

/**
 * Read a numeric theme index from URL parameters.
 * Preferred: themeid (short key). Backward compatible: themeIndex, theme_index, themeId/themeID, theme.
 * @returns {number|null} Parsed index or null if missing/invalid
 */
export const getThemeIndexFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    let raw = null;
    let isOneBased = false;

    if (params.has('themeid')) {
      raw = params.get('themeid');
      isOneBased = true; // short key uses 1-based indexing
    } else if (params.has('themeId')) {
      raw = params.get('themeId');
      isOneBased = true; // camelCase short key also 1-based
    } else if (params.has('themeID')) {
      raw = params.get('themeID');
      isOneBased = true; // Pascal-ish variant also 1-based
    } else if (params.has('theme')) {
      raw = params.get('theme');
    } else if (params.has('themeIndex')) {
      raw = params.get('themeIndex');
    } else if (params.has('theme_index')) {
      raw = params.get('theme_index');
    }

    if (raw === null || raw === '') return null;

    let idx = parseInt(raw, 10);
    if (Number.isNaN(idx)) return null;
    if (isOneBased) idx = idx - 1; // normalize to 0-based
    return idx;
  } catch (_) {
    return null;
  }
};

/**
 * Read option selections from URL query params and map them to config option keys.
 * Supports keys like o1, o2, o3... where the number corresponds to the sorted
 * order of Options (Option1..OptionN). Values can be:
 * - numeric: treated as zero-based index into the option's `values` array
 * - string: matched against option item `value` or display name strings
 * Returns an object of the form { OptionX: { value } } suitable for global options.
 * @param {Object} optionsCfg - The `SupportedModels.Options` object from config
 * @returns {Object} mapping of OptionX => { value }
 */
export const getOptionSelectionsFromUrl = (optionsCfg = {}) => {
  try {
    const params = new URLSearchParams(window.location.search);
    // Build ordered list of option keys based on numeric suffix (Option1..OptionN first)
    const entries = Object.entries(optionsCfg)
      .filter(([, def]) => def && Array.isArray(def.values) && def.values.length > 0)
      .sort(([a], [b]) => {
        const na = parseInt(String(a).replace(/[^0-9]/g, ''), 10) || 0;
        const nb = parseInt(String(b).replace(/[^0-9]/g, ''), 10) || 0;
        return na - nb;
      });
    const optionKeys = entries.map(([k]) => k);

    const result = {};

    // Helper: normalize value for def.values[i] (object or string)
    const normalizeVal = (item) => {
      if (item == null) return null;
      if (typeof item === 'object' && 'value' in item) return item.value;
      return item;
    };

    // Helper: find by string value or display name
    const findByString = (def, str) => {
      const s = String(str).trim();
      const vals = Array.isArray(def?.values) ? def.values : [];
      for (const v of vals) {
        const val = normalizeVal(v);
        if (val != null && String(val).trim() === s) return val;
        // fallback: match displayName string if present
        const dn = v?.displayName;
        if (typeof dn === 'string' && dn.trim() === s) return val;
        if (typeof dn === 'object') {
          const alt = dn['en'] || dn['cn'] || '';
          if (alt && String(alt).trim() === s) return val;
        }
      }
      return null;
    };

    // Iterate over potential params o1..oN (cap at 20 for sanity)
    for (let i = 1; i <= Math.min(20, optionKeys.length); i++) {
      const key = `o${i}`;
      if (!params.has(key)) continue;
      const raw = params.get(key);
      const optKey = optionKeys[i - 1];
      const def = optionsCfg[optKey] || {};
      const values = Array.isArray(def.values) ? def.values : [];

      let chosen = null;
      // Numeric index support (zero-based indexing, aligned with provided example o1=0)
      const idx = parseInt(raw, 10);
      if (!Number.isNaN(idx)) {
        if (idx >= 0 && idx < values.length) {
          chosen = normalizeVal(values[idx]);
        }
      }
      // String matching fallback
      if (chosen == null && typeof raw === 'string' && raw !== '') {
        const byStr = findByString(def, raw);
        if (byStr != null) chosen = byStr;
      }

      if (chosen != null) {
        result[optKey] = { value: chosen };
      }
    }

    return result;
  } catch (_) {
    return {};
  }
};

/**
 * Check if kiosk mode is enabled via URL parameter
 * @returns {boolean} True if kiosk mode is enabled
 */
export const isKioskMode = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const kioskParam = params.get('kiosk');
    return kioskParam === 'true' || kioskParam === '1';
  } catch (_) {
    return false;
  }
};
